import type { HTMLAttributes, ReactNode } from "react";
import type {
  HeadingProps as ReactAriaHeadingProps,
  PopoverProps as ReactAriaPopoverProps,
} from "react-aria-components";
import {
  OverlayArrow,
  Dialog as ReactAriaDialog,
  DialogTrigger as ReactAriaDialogTrigger,
  Heading as ReactAriaHeading,
  Popover as ReactAriaPopover,
  Pressable as ReactAriaPressable,
  type DialogTriggerProps as ReactAriaTriggerProps,
} from "react-aria-components";

import { cn, cv, type VariantProps } from "../utils";
import { NcIconExclamationTriangle, NcIconInformationCircle, NcIconTickCircle } from "./nc-icons";

export const ncPopoverClasses = {
  popover: cn(
    "rounded-sm bg-light border border-ui shadow-lg max-w-72 print:hidden",
    "[&>section]:rounded-sm [&>section]:focus-visible:outline-main [&>section]:focus-visible:outline-2 [&>section]:focus-visible:outline-offset-2",
    "data-[placement='top']:data-entering:animate-slide-in-top data-[placement='top']:data-exiting:animate-slide-out-top",
    "data-[placement='bottom']:data-entering:animate-slide-in-bottom data-[placement='bottom']:data-exiting:animate-slide-out-bottom",
    "data-[placement='right']:data-entering:animate-slide-in-right data-[placement='right']:data-exiting:animate-slide-out-right",
    "data-[placement='left']:data-entering:animate-slide-in-left data-[placement='left']:data-exiting:animate-slide-out-left"
  ),
  overlayArrow: "group",
  arrowSvg: cn(
    "block fill-light stroke-ui",
    "group-data-[placement='right']:rotate-90",
    "group-data-[placement='bottom']:rotate-180",
    "group-data-[placement='left']:rotate-270"
  ),
  body: "grid gap-4 p-4 overflow-auto",
  heading: {
    base: "font-bold leading-tight text-2xl flex items-center gap-1",
    variants: {
      default: "",
      danger: "text-danger",
      info: "text-main",
      success: "text-success",
    },
  },
};

export interface NcPopoverProps extends ReactAriaPopoverProps {
  children: ReactNode;
}

const NcPopover = ({ children, placement, className, ...props }: NcPopoverProps) => {
  return (
    <ReactAriaPopover
      data-nc="NcPopover"
      className={cn(ncPopoverClasses.popover, className)}
      placement={placement}
      {...props}
    >
      <OverlayArrow className={ncPopoverClasses.overlayArrow}>
        <svg width={12} height={12} viewBox="0 0 8 8" className={ncPopoverClasses.arrowSvg}>
          <path d="M0 0 L4 4 L8 0" />
        </svg>
      </OverlayArrow>
      <ReactAriaDialog>{children}</ReactAriaDialog>
    </ReactAriaPopover>
  );
};

type PopoverTriggerProps = ReactAriaTriggerProps;

const NcPopoverTrigger = ({ ...props }: PopoverTriggerProps) => {
  return <ReactAriaDialogTrigger data-nc="NcPopover.Trigger" {...props} />;
};

type PopoverBodyProps = HTMLAttributes<HTMLDivElement>;

const NcPopoverBody = ({ className, ...props }: PopoverBodyProps) => (
  <div data-nc="NcPopover.Body" className={cn(ncPopoverClasses.body, className)} {...props} />
);

const ncPopoverHeadingVariants = cv(ncPopoverClasses.heading.base, {
  variants: {
    variant: ncPopoverClasses.heading.variants,
  },
  defaultVariants: {
    variant: "default",
  },
});

interface NcPopoverHeadingProps
  extends ReactAriaHeadingProps, VariantProps<typeof ncPopoverHeadingVariants> {}

const NcPopoverHeading = ({ children, variant, className, ...props }: NcPopoverHeadingProps) => (
  <ReactAriaHeading
    data-nc="NcPopover.Heading"
    slot="title"
    className={cn(ncPopoverHeadingVariants({ variant }), className)}
    {...props}
  >
    {variant === "danger" && <NcIconExclamationTriangle className="size-6 flex-shrink-0" />}
    {variant === "info" && <NcIconInformationCircle className="size-6 flex-shrink-0" />}
    {variant === "success" && <NcIconTickCircle className="size-6 flex-shrink-0" />}
    {children}
  </ReactAriaHeading>
);

NcPopover.Trigger = NcPopoverTrigger;
NcPopover.Heading = NcPopoverHeading;
NcPopover.Body = NcPopoverBody;
NcPopover.Pressable = ReactAriaPressable;

export { NcPopover };
